import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Quota } from '../../../quotas/models/quota.interface';
import { QuotaService } from '../../../quotas/services/quota.service';
import { AuthService } from '../../../../core/services/auth.service';
import { extractApiError } from '../../../../core/utils/api-format';
import { KgToTonsPipe } from '../../../../shared/pipes/kg-to-tons.pipe';
import { SupplierTypePipe } from '../../../../shared/pipes/supplier-type.pipe';
import { QuotaSummaryBarComponent } from '../../../../shared/components/quota-summary-bar/quota-summary-bar';
import { QuotaDetailsDialogComponent } from '../../components/quota-details-dialog/quota-details-dialog';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';
import { DistributeDialogComponent } from '../../components/distribute-dialog/distribute-dialog.component';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { QuotaPreviewDialogComponent } from '../../components/quota-preview-dialog/quota-preview-dialog.component';
import { StageKey } from '../../../quotas/models/quota-preview.interface';

@Component({
  selector: 'app-destribution-quotas',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatSnackBarModule,
    KgToTonsPipe,
    SupplierTypePipe,
    QuotaSummaryBarComponent,
    TranslateModule
  ],
  templateUrl: './destribution-quotas.html',
  styleUrl: './destribution-quotas.css',
})
export class DestributionQuotas implements OnInit {
  private quotaService = inject(QuotaService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  quotas = signal<Quota[]>([]);
  loading = signal(true);
  loadingPreview = signal(false);
  loadingPdf = signal(false);
  error = signal<string | null>(null);
  activeTab = signal('all');

  // Tabs with translation keys
  tabKeys = [
    { value: 'all', labelKey: 'distributionQuotas.tabs.all' },
    { value: 'stage1', labelKey: 'distributionQuotas.tabs.stage1' },
    { value: 'stage2', labelKey: 'distributionQuotas.tabs.stage2' },
    { value: 'new_suppliers', labelKey: 'distributionQuotas.tabs.newSuppliers' },
    { value: 'remainder', labelKey: 'distributionQuotas.tabs.remainder' }
  ];

  getTabLabel(labelKey: string): string {
    return this.translate.instant(labelKey);
  }

  // Проверка роли - quota_manager может только просматривать
  isManager = computed(() => {
    return this.authService.hasRole('quota_viewer') || (this.authService.hasRole('quota_manager') && !this.authService.hasRole('quota_operator'));
  });

  ngOnInit(): void {
    this.loadQuotas();
  }

  loadQuotas(): void {
    this.loading.set(true);
    this.error.set(null);

    const params: any = { page: 1, size: 100 };

    // Add stage filter based on active tab
    const stageMap: Record<string, string> = {
      'stage1': 'STAGE_1',
      'stage2': 'STAGE_2',
      'new_suppliers': 'STAGE_FOR_NEW_SUPPLIERS',
      'remainder': 'STAGE_3'
    };

    if (stageMap[this.activeTab()]) {
      params.stage = stageMap[this.activeTab()];
    }

    this.quotaService.getAllQuotas(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.quotas.set(response.res.content || []);
        } else {
          this.error.set(response.msg || this.translate.instant('distributionQuotas.errors.loadingData'));
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading quotas:', err);
        this.error.set(this.translate.instant('distributionQuotas.errors.loadQuotas'));
        this.loading.set(false);
      }
    });
  }

  setActiveTab(tab: string): void {
    this.activeTab.set(tab);
    this.loadQuotas();
  }

  distribute(): void {
    const stageMap: Record<string, { stage: 'stage1' | 'stage2' | 'new_suppliers' | 'remainder'; titleKey: string }> = {
      'stage1': { stage: 'stage1', titleKey: 'distributionQuotas.dialogTitles.stage1' },
      'stage2': { stage: 'stage2', titleKey: 'distributionQuotas.dialogTitles.stage2' },
      'new_suppliers': { stage: 'new_suppliers', titleKey: 'distributionQuotas.dialogTitles.newSuppliers' },
      'remainder': { stage: 'remainder', titleKey: 'distributionQuotas.dialogTitles.remainder' }
    };

    const stageConfig = stageMap[this.activeTab()];
    if (!stageConfig) {
      this.snackBar.open(this.translate.instant('distributionQuotas.errors.selectStageDistribute'), 'OK', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }

    const stageData = {
      stage: stageConfig.stage,
      title: this.translate.instant(stageConfig.titleKey)
    };

    const dialogRef = this.dialog.open(DistributeDialogComponent, {
      width: '400px',
      maxWidth: '90vw',
      data: stageData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.loadQuotas();
      }
    });
  }

  changeStatusToRemainder(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('distributionQuotas.confirm.changeStatusTitle'),
        message: this.translate.instant('distributionQuotas.confirm.changeStatusMessage'),
        confirmText: this.translate.instant('distributionQuotas.confirm.yes'),
        cancelText: this.translate.instant('distributionQuotas.confirm.cancel')
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.loading.set(true);
        this.quotaService.changeQuotaStatusToResidual().subscribe({
          next: (response) => {
            this.loading.set(false);
            if (response.success) {
              this.snackBar.open(this.translate.instant('distributionQuotas.success.statusChanged'), 'OK', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['success-snackbar']
              });
              this.loadQuotas();
            } else {
              const errorMsg = extractApiError(response, this.translate.instant('distributionQuotas.errors.statusChangeError'), this.translate.currentLang);
              this.snackBar.open(errorMsg, 'OK', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
          },
          error: (err) => {
            this.loading.set(false);
            console.error('Error changing status to residual:', err);

            const errorMsg = extractApiError(err.error, this.translate.instant('distributionQuotas.errors.statusChangeFailed'), this.translate.currentLang);

            this.snackBar.open(errorMsg, 'OK', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            });
          }
        });
      }
    });
  }

  downloadPdf(quota: Quota): void {
    const stageMap: Record<string, StageKey> = {
      'stage1': 'STAGE_1',
      'stage2': 'STAGE_2',
      'new_suppliers': 'STAGE_FOR_NEW_SUPPLIERS',
      'remainder': 'STAGE_3'
    };

    const stage = stageMap[this.activeTab()];

    if (!stage) {
      this.snackBar.open(this.translate.instant('distributionQuotas.errors.selectStageDownload'), 'OK', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }

    this.loadingPdf.set(true);
    this.quotaService.downloadDistributionPreviewPdf(quota.id, stage).subscribe({
      next: (blob) => {
        this.loadingPdf.set(false);

        // Сервер может вернуть HTTP 200 с JSON-ошибкой вместо PDF
        if (!blob.type.includes('pdf')) {
          blob.text().then(text => {
            try {
              const json = JSON.parse(text);
              const errorMsg = extractApiError(json, this.translate.instant('distributionQuotas.errors.pdfDownloadFailed'), this.translate.currentLang);
              this.snackBar.open(errorMsg, 'OK', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            } catch {
              this.snackBar.open(this.translate.instant('distributionQuotas.errors.pdfDownloadFailed'), 'OK', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
          });
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const meatName = this.translate.currentLang === 'ky' ? quota.meatType.nameKy : quota.meatType.nameRu;
        const distributionWord = this.translate.instant('distributionQuotas.fileNames.distribution');
        const fileName = `${distributionWord}_${meatName}_${quota.year}_${this.getStageTitle(stage)}.pdf`;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.snackBar.open(this.translate.instant('distributionQuotas.success.pdfDownloaded'), 'OK', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
      },
      error: (err) => {
        this.loadingPdf.set(false);
        console.error('Error downloading PDF:', err);

        const errBody = err.error;
        const errorMsg = this.translate.instant('distributionQuotas.errors.pdfDownloadFailed');

        if (errBody instanceof Blob) {
          errBody.text().then(text => {
            try {
              const json = JSON.parse(text);
              const msg = extractApiError(json, errorMsg, this.translate.currentLang);
              this.snackBar.open(msg, 'OK', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            } catch {
              this.snackBar.open(errorMsg, 'OK', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
              });
            }
          });
          return;
        }

        this.snackBar.open(extractApiError(errBody, errorMsg, this.translate.currentLang), 'OK', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  getStageTitle(stage: StageKey): string {
    const titleKeys: Record<StageKey, string> = {
      'STAGE_1': 'distributionQuotas.fileNames.stage1',
      'STAGE_2': 'distributionQuotas.fileNames.stage2',
      'STAGE_3': 'distributionQuotas.fileNames.stage3',
      'STAGE_FOR_NEW_SUPPLIERS': 'distributionQuotas.fileNames.newSuppliers'
    };
    return this.translate.instant(titleKeys[stage]);
  }

  preview(quota: Quota): void {
    const stageMap: Record<string, StageKey> = {
      'stage1': 'STAGE_1',
      'stage2': 'STAGE_2',
      'new_suppliers': 'STAGE_FOR_NEW_SUPPLIERS',
      'remainder': 'STAGE_3'
    };

    const stage = stageMap[this.activeTab()];

    if (!stage) {
      this.snackBar.open(this.translate.instant('distributionQuotas.errors.selectStagePreview'), 'OK', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });
      return;
    }

    this.loadingPreview.set(true);
    this.quotaService.getQuotaPreview(quota.id, stage).subscribe({
      next: (response) => {
        this.loadingPreview.set(false);
        if (response.success && response.res) {
          const meatName = this.translate.currentLang === 'ky' ? quota.meatType.nameKy : quota.meatType.nameRu;
          this.dialog.open(QuotaPreviewDialogComponent, {
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            data: {
              data: response.res,
              stage: stage,
              quotaName: `${meatName} ${quota.year}`
            }
          });
        } else {
          this.snackBar.open(response.msg || this.translate.instant('distributionQuotas.errors.previewLoadError'), 'OK', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      },
      error: (err) => {
        this.loadingPreview.set(false);
        console.error('Error loading preview:', err);

        const errorMsg = extractApiError(err.error, this.translate.instant('distributionQuotas.errors.previewLoadFailed'), this.translate.currentLang);

        this.snackBar.open(errorMsg, 'OK', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  viewDetails(quota: Quota): void {
    const params = {
      page: 1,
      size: 100,
      quotaId: quota.id,
      status: 'APPROVED',
      deleted: false
    };

    this.quotaService.getAllApplications(params).subscribe({
      next: (response) => {
        if (response.success) {
          this.dialog.open(QuotaDetailsDialogComponent, {
            width: '95vw',
            maxWidth: '1400px',
            maxHeight: '90vh',
            data: {
              quota: quota,
              applications: response.res.content || []
            }
          });
        }
      },
      error: (err) => {
        console.error('Error loading applications:', err);
      }
    });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
